import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import ProductImage from "@/components/ProductImage";
import { useScrollLock } from "@/lib/scrollLock";
import {
  productImageLightboxFallback,
  productImageLightboxSrc,
} from "@/lib/images";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.12;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_ZOOM = 2;
const SWIPE_THRESHOLD = 50;
const OPEN_GESTURE_GRACE_MS = 420;

const distanceBetween = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +value.toFixed(2)));

const clampPan = (x, y, zoom, wrapEl) => {
  if (!wrapEl || zoom <= MIN_ZOOM) return { x: 0, y: 0 };

  const { width, height } = wrapEl.getBoundingClientRect();
  const maxX = (width * (zoom - 1)) / 2;
  const maxY = (height * (zoom - 1)) / 2;

  return {
    x: Math.min(maxX, Math.max(-maxX, x)),
    y: Math.min(maxY, Math.max(-maxY, y)),
  };
};

const ProductImageLightbox = ({
  open,
  onOpenChange,
  images,
  activeIndex,
  onActiveIndexChange,
  productName,
}) => {
  const hasMultiple = images.length > 1;
  const currentImage = images[activeIndex] || images[0];
  const imageWrapRef = useRef(null);
  const zoomRef = useRef(MIN_ZOOM);
  const panRef = useRef({ x: 0, y: 0 });
  const gestureRef = useRef({
    pointers: new Map(),
    mode: null,
    pinchStartDistance: 0,
    pinchStartZoom: MIN_ZOOM,
    dragStartX: 0,
    dragStartY: 0,
    dragPanX: 0,
    dragPanY: 0,
    lastTapTime: 0,
    swipeStartX: 0,
    swipeStartY: 0,
    lastX: 0,
    lastY: 0,
  });

  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isGesturing, setIsGesturing] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState("");
  const ignoreGesturesUntilRef = useRef(0);

  const applyZoom = useCallback((nextZoom) => {
    const clamped = clampZoom(nextZoom);
    zoomRef.current = clamped;
    setZoom(clamped);
    return clamped;
  }, []);

  const applyPan = useCallback((x, y, currentZoom = zoomRef.current) => {
    const wrap = imageWrapRef.current;
    const clamped = clampPan(x, y, currentZoom, wrap);
    panRef.current = clamped;
    setPan(clamped);
    return clamped;
  }, []);

  const resetView = useCallback(() => {
    zoomRef.current = MIN_ZOOM;
    panRef.current = { x: 0, y: 0 };
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
    gestureRef.current.mode = null;
    gestureRef.current.pointers.clear();
    setIsGesturing(false);
  }, []);

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    const next = activeIndex <= 0 ? images.length - 1 : activeIndex - 1;
    onActiveIndexChange(next);
  }, [activeIndex, images.length, hasMultiple, onActiveIndexChange]);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    const next = activeIndex >= images.length - 1 ? 0 : activeIndex + 1;
    onActiveIndexChange(next);
  }, [activeIndex, images.length, hasMultiple, onActiveIndexChange]);

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    ignoreGesturesUntilRef.current = Date.now() + OPEN_GESTURE_GRACE_MS;
  }, [open, activeIndex]);

  useEffect(() => {
    setLightboxSrc(productImageLightboxSrc(currentImage));
  }, [currentImage]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange, goPrev, goNext]);

  useEffect(() => {
    resetView();
  }, [activeIndex, open, resetView]);

  useEffect(() => {
    if (zoom <= MIN_ZOOM) {
      panRef.current = { x: 0, y: 0 };
      setPan({ x: 0, y: 0 });
      return;
    }
    applyPan(panRef.current.x, panRef.current.y, zoom);
  }, [zoom, applyPan]);

  useEffect(() => {
    if (!open) return undefined;

    const el = imageWrapRef.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const direction = e.deltaY > 0 ? -1 : 1;
      applyZoom(zoomRef.current + direction * ZOOM_STEP);
    };

    const pointerList = () => [...gestureRef.current.pointers.values()];

    const onPointerDown = (e) => {
      if (Date.now() < ignoreGesturesUntilRef.current) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();
      el.setPointerCapture(e.pointerId);

      const gesture = gestureRef.current;
      gesture.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      gesture.lastX = e.clientX;
      gesture.lastY = e.clientY;

      if (gesture.pointers.size === 1 && zoomRef.current <= MIN_ZOOM) {
        gesture.swipeStartX = e.clientX;
        gesture.swipeStartY = e.clientY;
      }

      if (gesture.pointers.size === 2) {
        const [a, b] = pointerList();
        gesture.mode = "pinch";
        gesture.pinchStartDistance = distanceBetween(a, b);
        gesture.pinchStartZoom = zoomRef.current;
        setIsGesturing(true);
        return;
      }

      if (gesture.pointers.size === 1 && e.pointerType === "touch") {
        const now = Date.now();
        if (now - gesture.lastTapTime < DOUBLE_TAP_MS) {
          if (zoomRef.current > MIN_ZOOM) {
            applyZoom(MIN_ZOOM);
            applyPan(0, 0, MIN_ZOOM);
          } else {
            applyZoom(DOUBLE_TAP_ZOOM);
          }
          gesture.lastTapTime = 0;
          gesture.mode = null;
          return;
        }
        gesture.lastTapTime = now;
      }

      if (zoomRef.current > MIN_ZOOM) {
        gesture.mode = "pan";
        gesture.dragStartX = e.clientX;
        gesture.dragStartY = e.clientY;
        gesture.dragPanX = panRef.current.x;
        gesture.dragPanY = panRef.current.y;
        setIsGesturing(true);
      }
    };

    const onPointerMove = (e) => {
      const gesture = gestureRef.current;
      if (!gesture.pointers.has(e.pointerId)) return;

      gesture.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      gesture.lastX = e.clientX;
      gesture.lastY = e.clientY;
      e.preventDefault();

      if (gesture.pointers.size === 2) {
        const [a, b] = pointerList();
        if (!gesture.pinchStartDistance) {
          gesture.pinchStartDistance = distanceBetween(a, b);
          gesture.pinchStartZoom = zoomRef.current;
        }
        const ratio = distanceBetween(a, b) / gesture.pinchStartDistance;
        const nextZoom = applyZoom(gesture.pinchStartZoom * ratio);
        applyPan(panRef.current.x, panRef.current.y, nextZoom);
        gesture.mode = "pinch";
        setIsGesturing(true);
        return;
      }

      if (gesture.mode === "pan" && gesture.pointers.size === 1) {
        const dx = e.clientX - gesture.dragStartX;
        const dy = e.clientY - gesture.dragStartY;
        applyPan(gesture.dragPanX + dx, gesture.dragPanY + dy, zoomRef.current);
      }
    };

    const onPointerEnd = (e) => {
      const gesture = gestureRef.current;
      if (!gesture.pointers.has(e.pointerId)) return;

      gesture.pointers.delete(e.pointerId);
      if (el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }

      if (gesture.pointers.size === 0) {
        if (
          zoomRef.current <= MIN_ZOOM
          && hasMultiple
          && gesture.mode !== "pinch"
        ) {
          const dx = gesture.lastX - gesture.swipeStartX;
          const dy = gesture.lastY - gesture.swipeStartY;
          if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.25) {
            if (dx < 0) goNext();
            else goPrev();
          }
        }
        gesture.mode = null;
        gesture.pinchStartDistance = 0;
        setIsGesturing(false);
        return;
      }

      if (gesture.pointers.size === 1 && zoomRef.current > MIN_ZOOM) {
        const remaining = pointerList()[0];
        gesture.mode = "pan";
        gesture.dragStartX = remaining.x;
        gesture.dragStartY = remaining.y;
        gesture.dragPanX = panRef.current.x;
        gesture.dragPanY = panRef.current.y;
      } else {
        gesture.mode = null;
        gesture.pinchStartDistance = 0;
        setIsGesturing(false);
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onPointerDown, { passive: false });
    el.addEventListener("pointermove", onPointerMove, { passive: false });
    el.addEventListener("pointerup", onPointerEnd);
    el.addEventListener("pointercancel", onPointerEnd);

    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerEnd);
      el.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [open, activeIndex, currentImage, hasMultiple, goPrev, goNext, applyZoom, applyPan]);

  if (!open || !images.length) return null;

  const imageCursor = zoom > MIN_ZOOM
    ? (isGesturing ? "grabbing" : "grab")
    : "zoom-in";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/85"
      role="dialog"
      aria-modal="true"
      aria-label={`${productName} photos`}
      data-testid="product-image-lightbox"
      onClick={() => onOpenChange(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(false);
        }}
        className="absolute right-4 top-4 z-20 p-2 text-white/90 transition hover:text-white sm:right-6 sm:top-6"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.5} />
      </button>

      <div className="flex h-full w-full items-center justify-center gap-3 p-4 sm:gap-6 sm:p-8">
        {hasMultiple && (
          <div
            className="hidden max-h-[82vh] shrink-0 flex-col gap-2 overflow-y-auto py-4 md:flex"
            data-testid="lightbox-thumbnails"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((img, idx) => {
              const selected = idx === activeIndex;
              return (
                <button
                  key={`${img}-${idx}`}
                  type="button"
                  onClick={() => onActiveIndexChange(idx)}
                  className={`h-[72px] w-14 shrink-0 overflow-hidden border-2 transition ${
                    selected
                      ? "border-rose-400 opacity-100"
                      : "border-transparent opacity-60 hover:opacity-90"
                  }`}
                  aria-label={`View image ${idx + 1}`}
                  data-testid={`lightbox-thumb-${idx}`}
                >
                  <ProductImage
                    src={img}
                    alt={`${productName} ${idx + 1}`}
                    sizes="56px"
                    className="h-full w-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        )}

        <div className="relative flex w-full max-h-[85vh] max-w-[min(92vw,780px)] items-center justify-center">
          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goPrev();
                }}
                className="absolute left-2 top-1/2 z-10 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white/95 backdrop-blur-sm transition hover:bg-black/60 sm:left-3 sm:h-11 sm:w-11"
                aria-label="Previous photo"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goNext();
                }}
                className="absolute right-2 top-1/2 z-10 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white/95 backdrop-blur-sm transition hover:bg-black/60 sm:right-3 sm:h-11 sm:w-11"
                aria-label="Next photo"
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
              </button>
            </>
          )}

          <span
            ref={imageWrapRef}
            onClick={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
            className="inline-flex max-h-[85vh] w-full max-w-full touch-pan-y select-none overflow-hidden sm:w-auto"
            style={{ cursor: imageCursor, WebkitTouchCallout: "none" }}
            data-testid="lightbox-image-zoom"
          >
            <span
              className="inline-flex w-full justify-center sm:w-auto"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: "center center",
                transition: isGesturing ? "none" : "transform 0.15s ease-out",
                willChange: isGesturing ? "transform" : "auto",
              }}
            >
              <img
                key={currentImage}
                src={lightboxSrc}
                alt={`${productName} ${activeIndex + 1}`}
                decoding="async"
                fetchPriority="high"
                draggable={false}
                className="max-h-[85vh] max-w-full object-contain pointer-events-none select-none"
                onError={() => setLightboxSrc(productImageLightboxFallback(currentImage))}
              />
            </span>
          </span>
        </div>

        {hasMultiple && (
          <div
            className="absolute bottom-4 left-1/2 flex max-w-[92vw] -translate-x-1/2 gap-2 overflow-x-auto px-2 pb-1 md:hidden"
            data-testid="lightbox-thumbnails-mobile"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((img, idx) => {
              const selected = idx === activeIndex;
              return (
                <button
                  key={`mobile-${img}-${idx}`}
                  type="button"
                  onClick={() => onActiveIndexChange(idx)}
                  className={`h-16 w-12 shrink-0 overflow-hidden border-2 transition ${
                    selected
                      ? "border-rose-400 opacity-100"
                      : "border-white/30 opacity-70"
                  }`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <ProductImage
                    src={img}
                    alt={`${productName} ${idx + 1}`}
                    sizes="48px"
                    className="h-full w-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ProductImageLightbox;
